import { useAuth } from '@/app/AuthContext.jsx';
import {
  useActiveLaneTasks,
  useDashboardInicio,
  useCanReadOrgProfile,
  useDocumentTitle,
} from '@/hooks';
import { MS_PER_DAY, formatChileanDateShort, toISODate } from '@/lib';
import { extractList } from '@/lib/apiList';
import SuscribirCausaModal from '@/pages/causas/SuscribirCausaModal';
import { useNuevaCausaForm } from '@/pages/causas/useNuevaCausaForm.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import DashboardView from './DashboardView';
import {
  buildAgenda,
  buildFocus,
  buildMetrics,
  enrichDiligencias,
} from './dashboardModel';

function formatHoy(now) {
  const s = new Date(now).toLocaleDateString('es-CL', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  // es-CL renders "lun, 03 ago 2026" — drop the comma to match the kicker format.
  return s.replace(',', '');
}

function formatFeedDate(iso) {
  return formatChileanDateShort(iso) ?? '—';
}

// The "Actuaciones recientes" feed shows the latest PJUD actings regardless of
// how recently the case moved: the endpoint windows `recientes` (default 30d)
// and returns the newest 10 within it, so a narrow window hides orgs whose
// last activity is older. A wide window makes it read as "latest 10", period.
const RECIENTES_WINDOW_DAYS = 365;

// Inicio page for `/`. Fetches the org's active diligencias + the PJUD feed,
// derives the operator-deck view-model client-side (no dashboard-specific
// backend), and wires navigation. All derivation lives in dashboardModel.
export default function Dashboard() {
  useDocumentTitle('Inicio');
  const { setBreadcrumb, setPills } = useOutletContext();
  const { organizationId, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // "Suscribir causa" opens SuscribirCausaModal in place, as /causas does
  // (REC-1792). The hook's own handleSubmit already invalidates
  // dashboard-inicio + cases.all on every caller
  // (invalidateAfterCreateCausa, #504), so onCreated here only closes.
  const [creating, setCreating] = useState(false);
  const onCausaCreated = useCallback(() => {
    setCreating(false);
  }, []);
  const {
    canAssign,
    cortes,
    tribunalesPorCorte,
    members: encargadoMembers,
    values: createValues,
    setField: setCreateField,
    resetForm: resetCreateForm,
    resetError: resetCreateError,
    handleSubmit: handleCreateCase,
    apiError: createError,
    isSubmitting: creatingSubmitting,
  } = useNuevaCausaForm({ onCreated: onCausaCreated });
  const closeCreate = useCallback(() => {
    setCreating(false);
    resetCreateError();
  }, [resetCreateError]);

  // Anchor "now" to mount so the agenda + relative deltas are stable across
  // re-renders within a session.
  const now = useMemo(() => Date.now(), []);

  useEffect(() => {
    setBreadcrumb(['Inicio']);
    setPills([]);
    return () => {
      setBreadcrumb([]);
      setPills([]);
    };
  }, [setBreadcrumb, setPills]);

  // Org-wide active diligencias — same source the Diligencias board uses.
  const { data: tasksData } = useActiveLaneTasks(organizationId);

  // PJUD actuaciones feed (+ active-case count) from the existing aggregate.
  // Wide window so the feed surfaces the latest actings even when the org's
  // recent activity predates the endpoint's 30-day default.
  const feedRange = useMemo(
    () => ({ startDate: toISODate(now - RECIENTES_WINDOW_DAYS * MS_PER_DAY) }),
    [now],
  );
  // The endpoint needs org:read-dashboard, which the Abogado template does not
  // carry on purpose (it mixes operational and financial panels). The API gives
  // an owner every permission of the org's capability and ignores the stored
  // array, so an owner always reads it; a member needs the key. While the
  // session has not resolved the membership neither holds and the request is
  // not made. Without access the view drops the feed and the active-causes
  // number.
  const dashboardAvailable = useCanReadOrgProfile();
  const {
    data: inicio,
    isLoading: feedLoading,
    isError: feedError,
  } = useDashboardInicio(organizationId, feedRange, {
    enabled: dashboardAvailable,
  });

  const enriched = useMemo(
    () => (tasksData ? enrichDiligencias(extractList(tasksData), now) : []),
    [tasksData, now],
  );

  const focus = useMemo(() => {
    const items = buildFocus(enriched);
    return items.map((f) => ({ ...f, onClick: () => navigate(f.route) }));
  }, [enriched, navigate]);

  const metrics = useMemo(
    () => buildMetrics(enriched, { causasActivas: inicio?.causas?.activas }),
    [enriched, inicio],
  );

  const agendaDays = useMemo(() => {
    const days = buildAgenda(enriched, now);
    return days.map((day) => ({
      ...day,
      items: day.items.map((it) => ({
        ...it,
        onClick: () => navigate(it.route),
      })),
    }));
  }, [enriched, now, navigate]);

  // rol → caseId, so a feed row can open the causa detail (chosen nav target).
  const rolToCaseId = useMemo(() => {
    const m = new Map();
    enriched.forEach((d) => {
      if (d.caseRol && d.caseId) m.set(d.caseRol, d.caseId);
    });
    return m;
  }, [enriched]);

  const recientes = inicio?.notificaciones?.recientes ?? [];
  const feedState = feedError
    ? 'error'
    : feedLoading
      ? 'loading'
      : recientes.length === 0
        ? 'empty'
        : 'ready';

  const feedRows = useMemo(
    () =>
      recientes.map((r, i) => {
        const caseId = r.caseRol ? rolToCaseId.get(r.caseRol) : null;
        const route = caseId ? `/causas/${caseId}` : '/causas';
        return {
          key: `${r.caseRol ?? 'x'}-${r.actingDate ?? i}`,
          id: r.caseRol || '—',
          primary: r.name || '—',
          secondary: null,
          date: formatFeedDate(r.actingDate),
          onClick: () => navigate(route),
        };
      }),
    [recientes, rolToCaseId, navigate],
  );

  const requierenAccion = metrics.requierenAccion ?? 0;

  return (
    <>
      <DashboardView
        hoy={formatHoy(now)}
        firstName={user?.firstName ?? ''}
        requierenAccion={requierenAccion}
        focus={focus}
        metrics={metrics}
        agendaDays={agendaDays}
        feedState={feedState}
        feedRows={feedRows}
        dashboardAvailable={dashboardAvailable}
        onNuevaDiligencia={() =>
          navigate('/diligencias/nueva', { state: { background: location } })
        }
        onSuscribirCausa={() => setCreating(true)}
        onGo={(route) => navigate(route)}
      />
      <SuscribirCausaModal
        open={creating}
        onClose={closeCreate}
        cortes={cortes}
        tribunalesPorCorte={tribunalesPorCorte}
        members={encargadoMembers}
        canAssign={canAssign}
        values={createValues}
        onChange={setCreateField}
        onClear={resetCreateForm}
        onSubmit={handleCreateCase}
        apiError={createError}
        isSubmitting={creatingSubmitting}
      />
    </>
  );
}
